import { useSyncExternalStore } from "react";

export interface PageActivitySnapshot {
  active: boolean;
  activation: number;
}

const listeners = new Set<() => void>();
let installed = false;
let pageHidden = false;
let snapshot: PageActivitySnapshot = {
  active: typeof document === "undefined" || document.visibilityState !== "hidden",
  activation: 0,
};

function publish(active: boolean) {
  if (snapshot.active === active) return;
  snapshot = {
    active,
    activation: active ? snapshot.activation + 1 : snapshot.activation,
  };
  for (const listener of listeners) listener();
}

function install() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  const visibility = () => {
    if (document.visibilityState !== "hidden") pageHidden = false;
    publish(!pageHidden && document.visibilityState !== "hidden");
  };
  document.addEventListener("visibilitychange", visibility);
  window.addEventListener("pagehide", () => {
    pageHidden = true;
    publish(false);
  });
  window.addEventListener("pageshow", () => {
    pageHidden = false;
    visibility();
  });
  visibility();
}

export function getPageActivity(): PageActivitySnapshot {
  install();
  return snapshot;
}

export function subscribePageActivity(listener: () => void) {
  install();
  if (!pageHidden) publish(document.visibilityState !== "hidden");
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePageActivity() {
  return useSyncExternalStore(subscribePageActivity, getPageActivity, getPageActivity);
}
